/**
 * Collection page — storefront filtering, sorting, layout switch, banner clamp
 * and pagination.
 *
 * Everything here is an enhancement over markup that already works. The filter
 * panel is a real GET form pointed at the collection URL with Shopify's own
 * parameter names, so with JavaScript off a shopper still filters, sorts and
 * pages through the collection; this file only replaces the full page load with
 * a Section Rendering API fetch and a swap of the grid.
 *
 * Opening/closing the drawer, the group disclosures and the Escape key all come
 * from theme.js (window.Theme.Drawers / initAccordions); this file owns only
 * what happens inside the panel and to the grid.
 *
 * ES5 only — no build step.
 */
(function () {
  'use strict';

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  var VIEWS = ['1', '2', '3'];
  var STORAGE_KEY = 'ediots:collection-view';

  var section = $('[data-collection]');
  if (!section) return;

  var sectionId = section.getAttribute('data-section-id');
  var root = $('[data-collection-root]', section);

  /* ------------------------------------------------------------------ *
   * Layout switch
   *
   * The density lives on `data-view` and the stylesheet does the rest, so
   * switching is one attribute write. It is remembered per browser because a
   * shopper who has chosen the roomy layout means it for the next collection
   * too, not just this one.
   * ------------------------------------------------------------------ */
  function applyView(view) {
    if (!root) return;
    root.setAttribute('data-view', view);
    try { window.localStorage.setItem(STORAGE_KEY, view); } catch (e) { /* private mode */ }
  }

  (function initView() {
    if (!root) return;
    var stored = null;
    try { stored = window.localStorage.getItem(STORAGE_KEY); } catch (e) { stored = null; }
    if (stored && VIEWS.indexOf(stored) > -1) root.setAttribute('data-view', stored);

    var button = $('[data-view-switch]', section);
    if (!button) return;
    button.addEventListener('click', function () {
      var current = root.getAttribute('data-view') || '2';
      var next = VIEWS[(VIEWS.indexOf(current) + 1) % VIEWS.length];
      applyView(next);
    });
  })();

  /* ------------------------------------------------------------------ *
   * Banner clamp
   *
   * The control only appears when the copy actually overflows its three lines —
   * a one-sentence collection description gets no dead "read more" under it.
   * ------------------------------------------------------------------ */
  (function initBanner() {
    var text = $('[data-banner-text]', section);
    var toggle = $('[data-banner-toggle]', section);
    if (!text || !toggle) return;

    if (text.scrollHeight <= text.clientHeight + 1) {
      toggle.remove();
      text.removeAttribute('data-clamped');
      return;
    }
    toggle.hidden = false;

    toggle.addEventListener('click', function () {
      var expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      if (expanded) {
        text.setAttribute('data-clamped', 'true');
        toggle.textContent = toggle.getAttribute('data-label-more') || 'read more';
      } else {
        text.removeAttribute('data-clamped');
        toggle.textContent = toggle.getAttribute('data-label-less') || 'read less';
      }
    });
  })();

  /* ------------------------------------------------------------------ *
   * Filtering and sorting
   * ------------------------------------------------------------------ */
  var form = $('[data-filter-form]');
  var productsId = 'CollectionProducts-' + sectionId;
  var pending = null;

  /* The form's own state as a query string, minus empty fields — an empty price
   * box must not become `filter.v.price.gte=` in the URL, which Shopify reads
   * as a real (and impossible) bound. */
  function formQuery() {
    if (!form) return '';
    var parts = [];
    var fields = $$('input, select', form);
    for (var i = 0; i < fields.length; i++) {
      var field = fields[i];
      if (!field.name || field.disabled) continue;
      if ((field.type === 'checkbox' || field.type === 'radio') && !field.checked) continue;
      if (field.value === '') continue;
      parts.push(encodeURIComponent(field.name) + '=' + encodeURIComponent(field.value));
    }
    return parts.join('&');
  }

  function setBusy(busy) {
    var container = document.getElementById(productsId);
    if (container) container.setAttribute('aria-busy', busy ? 'true' : 'false');
    if (root) root.setAttribute('data-loading', busy ? 'true' : 'false');
  }

  /* Swap in a freshly rendered section: the grid, the filter panel (its counts
   * and disabled values change with every choice) and the active-filter badge.
   *
   * `document.getElementById` is re-queried against the parsed response rather
   * than held from before, because the whole subtree has been replaced. */
  function render(html) {
    var parsed = new DOMParser().parseFromString(html, 'text/html');

    var freshProducts = parsed.getElementById(productsId);
    var liveProducts = document.getElementById(productsId);
    if (freshProducts && liveProducts) liveProducts.innerHTML = freshProducts.innerHTML;

    var freshForm = parsed.querySelector('[data-filter-form]');
    var liveForm = $('[data-filter-form]');
    if (freshForm && liveForm) {
      /* Which groups the shopper has collapsed is their state, not the
       * server's — carry it across the swap. */
      var collapsed = {};
      $$('[data-filter-block]', liveForm).forEach(function (block, i) {
        collapsed[i] = block.getAttribute('aria-expanded');
      });

      liveForm.innerHTML = freshForm.innerHTML;
      form = liveForm;

      $$('[data-filter-block]', liveForm).forEach(function (block, i) {
        if (collapsed[i] === 'false') {
          block.setAttribute('aria-expanded', 'false');
          var toggle = $('[data-accordion-toggle]', block);
          if (toggle) toggle.setAttribute('aria-expanded', 'false');
        }
      });
    }

    var freshBadge = parsed.querySelector('[data-filter-indicator]');
    var liveBadge = $('[data-filter-indicator]');
    if (freshBadge && liveBadge) liveBadge.textContent = freshBadge.textContent;

    if (window.Theme && window.Theme.refresh) window.Theme.refresh();
  }

  function apply(pushState) {
    if (!form) return;
    var query = formQuery();
    var base = form.getAttribute('action') || window.location.pathname;
    var url = query ? base + '?' + query : base;

    if (pending) pending.abort();
    pending = typeof AbortController === 'function' ? new AbortController() : null;
    setBusy(true);

    var fetchUrl = url + (query ? '&' : '?') + 'section_id=' + sectionId;

    fetch(fetchUrl, pending ? { signal: pending.signal } : undefined)
      .then(function (response) {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then(function (html) {
        render(html);
        if (pushState !== false) window.history.pushState({ filtered: true }, '', url);
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return;
        /* A failed fetch falls back to what would have happened anyway. */
        window.location.href = url;
      })
      .then(function () { setBusy(false); });
  }

  if (form && typeof fetch === 'function' && typeof DOMParser === 'function') {
    form.addEventListener('submit', function (event) {
      event.preventDefault();
      apply();
    });

    /* Checkboxes and radios apply immediately; a number field waits for the
     * shopper to finish typing, so `12` on the way to `120` is not a request. */
    var debounce;
    form.addEventListener('change', function (event) {
      var field = event.target;
      if (!field || !field.name) return;
      if (field.type === 'number') return;
      apply();
    });
    form.addEventListener('input', function (event) {
      var field = event.target;
      if (!field || field.type !== 'number') return;
      window.clearTimeout(debounce);
      debounce = window.setTimeout(apply, 500);
    });

    /* "Clear all" is a real link to the bare collection URL, so it only needs
     * intercepting to keep the drawer open. */
    document.addEventListener('click', function (event) {
      var clear = event.target.closest && event.target.closest('[data-filter-clear]');
      if (!clear) return;
      event.preventDefault();
      $$('input', form).forEach(function (field) {
        if (field.type === 'checkbox') field.checked = false;
        else if (field.type === 'number' || field.type === 'text') field.value = '';
      });
      apply();
    });

    window.addEventListener('popstate', function () {
      /* Back and forward re-fetch from the URL rather than the form, which is
       * still showing the state the shopper just navigated away from. */
      var query = window.location.search.replace(/^\?/, '');
      var fetchUrl = window.location.pathname +
        '?' + (query ? query + '&' : '') + 'section_id=' + sectionId;
      setBusy(true);
      fetch(fetchUrl)
        .then(function (response) { return response.text(); })
        .then(render)
        .catch(function () { window.location.reload(); })
        .then(function () { setBusy(false); });
    });
  }

  /* ------------------------------------------------------------------ *
   * Pagination
   *
   * The numbered links stay exactly as rendered. Only "next" is upgraded, into
   * an append, so a shopper working down a long collection keeps their place
   * instead of jumping to the top of page 2.
   * ------------------------------------------------------------------ */
  document.addEventListener('click', function (event) {
    var next = event.target.closest && event.target.closest('[data-pagination-next]');
    if (!next) return;
    if (typeof fetch !== 'function' || typeof DOMParser !== 'function') return;
    event.preventDefault();

    var url = next.getAttribute('href');
    var join = url.indexOf('?') > -1 ? '&' : '?';
    next.setAttribute('aria-busy', 'true');

    fetch(url + join + 'section_id=' + sectionId)
      .then(function (response) { return response.text(); })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var freshGrid = parsed.querySelector('[data-collection-grid]');
        var liveGrid = $('[data-collection-grid]');
        if (freshGrid && liveGrid) {
          while (freshGrid.firstChild) liveGrid.appendChild(freshGrid.firstChild);
        }

        var freshNav = parsed.querySelector('[data-pagination]');
        var liveNav = $('[data-pagination]');
        if (liveNav) {
          if (freshNav) liveNav.innerHTML = freshNav.innerHTML;
          else liveNav.remove();
        }

        window.history.replaceState({}, '', url);
        if (window.Theme && window.Theme.refresh) window.Theme.refresh();
      })
      .catch(function () { window.location.href = url; });
  });
})();
